import React from "react";
import SvgIcon from "../SvgIcon";
import { S3, S3_CDN_DOMAIN } from "../../constants";
import { isActiveUserTheCoach } from "../../utils/user-helpers";
const moment = require("moment");
import _ from "lodash";
import convert from "convert-units";
import { formattedTarget, getSetCounts } from "../../utils/actuals";
import _round from "lodash/round";
import { getExerciseName } from "../modals/learn-more/blockHelpers";

const actionTextTypes = {
  "finished-onboarding": (action) => (
    <p>
      {action.athleteName || action.clientName} has successfully completed their
      account onboarding. Build out their profile{" "}
      <a href={actionHrefs[action.actionType](action)}>here.</a>
    </p>
  ),
  bodyPhotos: (action) => {
    const photosAmount = Object.keys(action.metadata?.bodyPhotos || {}).length;
    return (
      <p>
        {`${
          action.athleteName || action.clientName || action.clientName
        } uploaded ${photosAmount} photo${photosAmount > 1 ? "s" : ""}`}{" "}
        for {formatDate(action.metadata?.date)}.
      </p>
    );
  },
  bodyWeight: (action, activeUser) => {
    const bodyWeight =
      activeUser.units === "imperial"
        ? convert(action.metadata?.bodyWeight).from("kg").to("lb")
        : action.metadata?.bodyWeight;
    const unit = activeUser.units === "imperial" ? "Lb" : "Kg";

    return (
      <p>
        {action.athleteName || action.clientName} logged bodyweight of
        <b>{` ${_.round(bodyWeight, 1)} ${unit}`}</b>
        {" for "}
        <b>{formatDate(action.metadata?.date)}</b>
      </p>
    );
  },
  bodyMeasurements: (action) => (
    <p>
      {action.athleteName || action.clientName} logged body measurements{" "}
      {" for "}
      <b>{formatDate(action.metadata?.date)}</b>:
    </p>
  ),
  nutrients: (action) => (
    <p>
      {action.athleteName || action.clientName} logged nutrition
      {action.metadata?.date ? (
        <span>
          {" "}
          for <b>{formatDate(action.metadata?.date)}:</b>
        </span>
      ) : (
        ":"
      )}
    </p>
  ),
  steps: (action) => (
    <p>
      {action.athleteName || action.clientName} logged steps {" for "}
      <b>{formatDate(action.metadata?.date)}</b>.
    </p>
  ),
  sleep: (action) => (
    <p>
      {action.athleteName || action.clientName} logged sleep {" for "}
      <b>{formatDate(action.metadata?.date)}</b>.
    </p>
  ),
  hrv: (action) => (
    <p>
      {action.athleteName || action.clientName} logged HRV{" "}
      {action.metadata?.hrv}ms for <b>{formatDate(action.metadata?.date)}</b>.
    </p>
  ),
  readiness: (action) => (
    <p>
      {action.athleteName || action.clientName} completed their readiness
      questionnaire with a score of{" "}
      <b>
        {action.metadata?.readiness &&
          Object.values(action.metadata?.readiness)?.reduce(
            (prev, next) => prev + next
          )}
        /25
      </b>
      {" for "}
      <b>{formatDate(action.metadata?.date)}</b>.
    </p>
  ),
  "completed-accountabilities": (action) => (
    <p>
      {action.athleteName || action.clientName} marked all accountabilities as
      completed on {formatDate(action.metadata?.date)}
    </p>
  ),
  "completed-check-in": (action) => (
    <p>
      {action.athleteName || action.clientName} completed their check-in on{" "}
      {formatDate(action.metadata?.checkInDate)}
    </p>
  ),
  "completed-onboarding-check-in": (action) => (
    <p>
      {action.athleteName || action.clientName} completed their onboarding
      questionnaire on {formatDate(action.metadata.checkInDate)}
    </p>
  ),
  "swipe-completed-workout": (action) => (
    <p>
      {action.athleteName || action.clientName} completed their{" "}
      {action.metadata?.date ? (
        <b>{` ${formatDate(action.metadata?.date)} `}</b>
      ) : (
        ""
      )}{" "}
      workout
    </p>
  ),
  sessionRPE: (action) => (
    <p>
      {action.athleteName || action.clientName} rated their{" "}
      <b> {formatDate(action.metadata?.date)}</b> workout as{" "}
      {action.metadata?.sessionRPE ? `${action.metadata?.sessionRPE} ` : " "}
      Session RPE.
    </p>
  ),
  "uploaded-video-set": (action) => (
    <p>
      {action.athleteName || action.clientName} uploaded a video to a set
      {action.metadata?.date ? (
        <>
          {" "}
          on their <b>{` ${formatDate(action.metadata?.date)}`}</b> workout.
        </>
      ) : (
        "."
      )}
    </p>
  ),
  "commented-set": (action) => (
    <p>
      {action.athleteName || action.clientName} commented on a set on their{" "}
      {action.metadata?.date ? (
        <>
          <b>{` ${formatDate(action.metadata?.date)}`}</b> workout.
        </>
      ) : (
        "."
      )}
    </p>
  ),
  "commented-workout": (action) => (
    <p>
      {action.athleteName || action.clientName} commented on their{" "}
      <b>{formatDate(action.metadata?.date)} </b> workout.
    </p>
  ),
  "commented-check-in": (action) => (
    <p>
      {action.athleteName || action.clientName} commented on their{" "}
      <b>{formatDate(action.metadata?.date)} </b> check-in review.
    </p>
  ),
  "watched-check-in-video": (action) => (
    <p>
      {action.athleteName || action.clientName} watched check-in video from{" "}
      <b>{formatDate(action.metadata?.checkInDate)}</b>
    </p>
  ),
  "added-exercise": (action) => (
    <p>
      {action.athleteName || action.clientName} added an exercise to their{" "}
      <b>{formatDate(action.metadata?.date)}</b> workout:
    </p>
  ),
  "deleted-exercise": (action) => (
    <p>
      {action.athleteName || action.clientName} deleted an exercise from their{" "}
      <b>{formatDate(action.metadata?.date)}</b> workout.
    </p>
  ),
  // {name} started the program {program name} using {continuous add | full add}
  "added-calendar-program": (action) =>
    action.metadata?.programName ? (
      <p>
        {action.athleteName || action.clientName} started the program{" "}
        <b>{action.metadata?.programName}</b>{" "}
        {action.metadata?.continuousAdd
          ? ` using continuous add.`
          : ` on ${formatDate(action.metadata?.date)}.`}
      </p>
    ) : (
      <p>
        {action.athleteName || action.clientName} added a program to their
        calendar{" "}
        {action.metadata?.date ? (
          <>
            starting on <b>{formatDate(action.metadata?.date)}</b>.
          </>
        ) : (
          "."
        )}
      </p>
    ),
  "moved-workout": (action) => (
    <p>
      {action.athleteName || action.clientName} moved a workout from{" "}
      {formatDate(action.metadata?.from)?.date} to{" "}
      {formatDate(action.metadata?.to)}.
    </p>
  ),
  "removed-coach": (action) => (
    <p>
      {action.athleteName || action.clientName} has removed you as their coach.
    </p>
  ),
};
const actionHrefs = {
  "finished-onboarding": (action) =>
    `/athlete-portal/profile?athlete=${action.clientId}&section=dashboard`,
  bodyPhotos: (action) =>
    `/athlete-portal/metrics/progress-photos?date=${moment().format(
      "YYYYMMDD"
    )}&athlete=${action.clientId}`,
  bodyWeight: (action) =>
    `/athlete-portal/metrics/bodyweight?date=${moment().format(
      "YYYYMMDD"
    )}&athlete=${action.clientId}`,
  bodyMeasurements: (action) =>
    `/athlete-portal/metrics/body-measurements?date=${moment().format(
      "YYYYMMDD"
    )}&athlete=${action.clientId}`,
  nutrients: (action) => `/athlete-portal/nutrition?athlete=${action.clientId}`,
  steps: (action) =>
    `/athlete-portal/metrics/step-count?date=${moment().format(
      "YYYYMMDD"
    )}&athlete=${action.clientId}`,
  sleep: (action) =>
    `/athlete-portal/metrics/sleep-time?date=${moment().format(
      "YYYYMMDD"
    )}&athlete=${action.clientId}`,
  hrv: (action) =>
    `/athlete-portal/metrics/hrv?date=${moment().format("YYYYMMDD")}&athlete=${
      action.clientId
    }`,
  readiness: (action) =>
    `/athlete-portal/metrics/readiness-rate?date=${moment().format(
      "YYYYMMDD"
    )}&athlete=${action.clientId}`,
  "completed-accountabilities": (action) =>
    `/athlete-portal/metrics/accountability?date=${moment().format(
      "YYYYMMDD"
    )}&athlete=${action.clientId}`,
  "completed-check-in": (action) =>
    `/athlete-portal/metrics/check-in-summary?checkInDate=${moment(
      action.metadata?.checkInDate
    ).format("YYYY-MM-DD")}&athlete=${action.clientId}`,
  "completed-onboarding-check-in": (action) =>
    `/athlete-portal/profile/?athlete=${action.clientId}`,
  "swipe-completed-workout": (action) =>
    `/athlete-portal/calendar?date=${
      action.metadata?.date
        ? moment(action.metadata?.date).utc().format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD")
    }&athlete=${action.clientId}&view=day`,
  sessionRPE: (action) =>
    `/athlete-portal/metrics/session-rpe?date=${moment().format(
      "YYYYMMDD"
    )}&athlete=${action.clientId}`,
  "uploaded-video-set": (action) => {
    return `/athlete-portal/calendar?view=day&date=${
      action.metadata?.date
        ? moment(String(action.metadata?.date)).utc().format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD")
    }&athlete=${action.clientId}`;
  },
  "uploaded-video-set-video": (action) => {
    const { date, videoData: { videoOriginal, mediaId } = {} } =
      action.metadata;
    return `/athlete-portal/calendar?athlete=${
      action.clientId
    }&view=day&date=${moment(date)
      .utc()
      .format(
        "YYYY-MM-DD"
      )}&modal[type]=exercise-set-video&mediaId=${mediaId}&originalId=${videoOriginal}`;
  },
  "commented-set": (action) =>
    `/athlete-portal/calendar?date=${
      action.metadata?.date
        ? moment(action.metadata?.date).utc().format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD")
    }&athlete=${action.clientId}&view=day`,
  "commented-workout": (action) =>
    `/athlete-portal/calendar?date=${
      action.metadata?.date
        ? moment(action.metadata?.date).utc().format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD")
    }&athlete=${action.clientId}&view=day`,
  "commented-check-in": (action) => ``,
  "watched-check-in-video": (action) =>
    `/athlete-portal/metrics/check-in-summary?checkInDate=${moment(
      action.metadata?.checkInDate
    ).format("YYYY-MM-DD")}&athlete=${action.clientId}`,
  "added-exercise": (action) =>
    `/athlete-portal/calendar?athlete=${action.clientId}`,
  "deleted-exercise": (action) =>
    `/athlete-portal/calendar?athlete=${action.clientId}`,

  "added-calendar-program": (action) =>
    `/athlete-portal/calendar?athlete=${action.clientId}`,
  "moved-workout": (action) =>
    `/athlete-portal/calendar?athlete=${action.clientId}`,
};

function formatTimestamp(timestamp) {
  const date = moment(timestamp);
  const timeLabel = date.format("h:mm a"); // Example: "12:05 pm"

  return { time: timeLabel };
}
function formatDate(inputDate) {
  // Parse the input date

  const date = moment(String(inputDate)).utc();

  // Check if the date is valid
  if (!date.isValid()) {
    return "Invalid date";
  }

  // Format the date
  return date.format("MMM Do");
}

const ItemWrapper = ({ action, activeUser, fromMetric, withWeight }) => {
  const { actionType, timestamp } = action;
  const { time } = formatTimestamp(timestamp);

  const athleteIsFromOtherOrganizationCoach =
    activeUser.organizationId &&
    action.coachId &&
    !isActiveUserTheCoach(activeUser._id, action.coachId);
  if (!actionTextTypes[actionType]) return null;
  const isPush = !!action.isPush;
  const isOnlyPress = action.isPress && !action.athleteName;
  const redirectUrl =
    isPush || isOnlyPress
      ? undefined
      : actionHrefs[actionType]
      ? `${actionHrefs[actionType](action)}${
          athleteIsFromOtherOrganizationCoach
            ? `&&organizationCoach=${action.coachId}`
            : ""
        }`
      : undefined;

  return (
    <a
      href={redirectUrl || undefined}
      style={isPush || isOnlyPress ? { cursor: "default" } : {}}
    >
      <header>
        <SvgIcon
          src={action.athleteAvatar}
          classNames="avatar"
          noFilterOnLight={true}
        />
        {actionTextTypes[actionType](action, activeUser)}
        <p className="ml-auto text-nowrap">{time}</p>
      </header>

      <ExtraContent
        action={action}
        activeUser={activeUser}
        fromMetric={fromMetric}
        withWeight={withWeight}
      />
    </a>
  );
};

export default ItemWrapper;

const ExtraContent = ({ action, activeUser, fromMetric, withWeight }) => {
  if (action.actionType === "bodyPhotos") {
    const { front, back, side } = action.metadata?.bodyPhotos || {};
    return (
      <div className="pp-content">
        {front && <img src={S3.toCDN(front.s3Path)} />}
        {back && <img src={S3.toCDN(back.s3Path)} />}
        {side && <img src={S3.toCDN(side.s3Path)} />}
      </div>
    );
  }
  if (action.actionType === "nutrients") {
    const { proteins, fats, fibers, carbs } = action.metadata?.nutrients || {};
    const proteinsKcal = proteins ? proteins * 4 : 0;
    const carbsKcal = carbs ? carbs * 4 : 0;
    const fatsKcal = fats ? fats * 9 : 0;
    const kcal = proteinsKcal + carbsKcal + fatsKcal;
    const roundValue = (value) => _.round(value, 1);
    return (
      <div className=" flex items-center gap-4 bg-[--white-05] rounded-[--border-radius] p-4 mt-2">
        {proteins && (
          <span className="text-[--protein]">P: {roundValue(proteins)}g</span>
        )}
        {carbs && (
          <span className="text-[--carbs]">C: {roundValue(carbs)}g</span>
        )}
        {fats && <span className="text-[--fat]">F: {roundValue(fats)}g</span>}
        {fibers && <span>Fiber: {roundValue(fibers)}g</span>}
        Kcal:{` ${roundValue(kcal)}`}
      </div>
    );
  }
  if (action.metadata?.note || action.metadata?.comment) {
    return (
      <div className="bg-[--white-05] rounded-[--border-radius] p-4 mt-2">
        <p>{action.metadata?.note || action.metadata?.comment}</p>
      </div>
    );
  }
  if (
    action.actionType === "bodyMeasurements" &&
    action.metadata?.bodyMeasurements
  ) {
    const parts = {
      chest: "Chest",
      rightArm: "Right Arm",
      leftArm: "Left Arm",
      neck: "Neck",
      waist: "Waist",
      hips: "Hips",
      leftThigh: "Left Thigh",
      rightThigh: "Right Thigh",
      leftCalf: "Left Calf",
      rightCalf: "Right Calf",
    };
    const { bodyMeasurements } = action.metadata;
    const unit = activeUser.units === "imperial" ? "inch" : "cm";

    return (
      <div className=" flex flex-wrap items-center gap-4 bg-[--white-05] rounded-[--border-radius] p-4 mt-2">
        {Object.keys(bodyMeasurements).map((key) => {
          if (!parts[key]) return null;
          else {
            const value =
              unit === "cm"
                ? bodyMeasurements[key]
                : convert(bodyMeasurements[key]).from("cm").to("in");
            return (
              <span key={parts[key]}>
                {parts[key]}:{` ${_.round(value, 2)} ${unit}`}
              </span>
            );
          }
        })}
      </div>
    );
  }
  if (action.actionType === "uploaded-video-set") {
    const { videoData } = action.metadata;
    if (!videoData) return null;
    const { videoOriginal } = videoData;

    const url = `${S3_CDN_DOMAIN}exercise_set_videos/${action.clientId}/${videoOriginal}`;
    return (
      <a
        href={actionHrefs["uploaded-video-set-video"](action)}
        onClick={(e) => e.stopPropagation()}
        className="pp-content"
      >
        <SvgIcon
          src="/img/new-icons/play-white.svg"
          classNames="play-icon"
          noFilterOnLight={true}
        />
        <video
          width="100"
          height="180"
          controls={false}
          controlsList="nodownload"
          disablePictureInPicture
          src={url}
          className="aspect-16/10 rounded-[0.5rem]"
        />
      </a>
    );
  }
  if (
    action.actionType === "added-exercise" ||
    action.actionType === "deleted-exercise"
  ) {
    const { exercise } = action.metadata;
    if (!exercise) return null;
    const { units } = activeUser;
    const precision = units === "metric" ? 2 : 1;
    const intensityMeasure =
      _.get(activeUser, "intensityMeasure", "rpe") === "rpe" ? "RPE" : "RiR";
    const isRiR = intensityMeasure === "RiR";
    return (
      <div className=" bg-[--white-05] rounded-[--border-radius] p-4 mt-2">
        <p className="mb-2">{getExerciseName(exercise)}</p>
        {exercise.sets.map((elem, idx) => {
          const load = elem.load
            ? _round(fromMetric(elem.load, units), precision)
            : 0;
          const targetLoad = !!load && withWeight(load, units);
          return (
            <div
              className="exercise-element-set-wrapper"
              key={`${exercise._id}${idx}`}
            >
              <div className="mb-1 font-[--font-size-sm] text-[--white-70]">
                {formattedTarget(
                  elem,
                  exercise.type === "primary" || exercise.type === "secondary",
                  elem.load ? `${_round(elem.percentRepMax, 1)}` : "",
                  "metric",
                  targetLoad,
                  intensityMeasure,
                  isRiR
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};
